'use client';

import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import {
  type CardFactoryProps,
  getPatientOverviewCard,
} from '@ctw/shared/content/overview/overview-card-factory';
import { useOverviewSummarizationCard } from '@ctw/shared/content/summarization/use-overview-summarization-card';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';

export type OverviewResources =
  | 'conditions'
  | 'referrals'
  | 'medications'
  | 'diagnostics'
  | 'encounters'
  | 'vitals'
  | 'risk-gaps';

export type PatientOverviewProps = {
  className?: ClassName;
  onSeeAllResources: (resource: OverviewResources) => void;
  onAfterDetailsDrawerClosed?: () => void;
  onOpenDetailsDrawer?: (c: ConditionModel) => void;
  conditionIdForDetailsDrawer?: string;
};

export const PatientOverview = withErrorBoundary({
  boundaryName: 'PatientOverview',
  Component: ({
    className,
    onSeeAllResources,
    onAfterDetailsDrawerClosed,
    onOpenDetailsDrawer,
    conditionIdForDetailsDrawer,
  }: PatientOverviewProps) => {
    const builderConfig = useBuilderConfig();
    const hasReferralsFeature = useFeatureFlag('ctw-referrals');
    const hasRiskGapsFeature = useFeatureFlag('ctw-patient-risk-opportunities');

    const summarizationCard = useOverviewSummarizationCard();

    // Use the new array-based cards configuration
    const cardFactoryProps: CardFactoryProps = {
      onSeeAllResources,
      onAfterDetailsDrawerClosed,
      onOpenDetailsDrawer,
      conditionIdForDetailsDrawer,
    };

    const enabledCards = builderConfig.patientOverviewPage?.cards
      // filter out cards guarded by feature flag
      .filter((card) => {
        if (card.type === 'referrals' && !hasReferralsFeature) {
          return false;
        }
        if (card.type === 'risk-gaps' && !hasRiskGapsFeature) {
          return false;
        }
        return true;
      })
      .map((cardConfig, index) => getPatientOverviewCard(cardConfig.type, index, cardFactoryProps))
      .filter(Boolean);

    if (enabledCards?.length === 0) {
      return <></>;
    }

    return (
      <div className={twx('@container/cards space-y-3', className)}>
        {summarizationCard}
        <div
          className={tw`grid @2xl/cards:grid-cols-2 @5xl/cards:grid-cols-3 @7xl/cards:grid-cols-4 grid-cols-1 gap-3`}
        >
          {enabledCards}
        </div>
      </div>
    );
  },
});
